<script setup lang="ts">
	import { computed } from 'vue'
	import { type LinkRouteMeta, type RouteRecordRaw, RouterLink, useRouter } from 'vue-router'
	const router = useRouter()

	const parentLinks = computed<RouteRecordRaw[]>(() => {
		return router.options.routes?.filter((route: RouteRecordRaw) => {
			if (route.meta) {
				const linkNavBar = (route.meta as LinkRouteMeta).linkNavBar
				if (typeof linkNavBar === 'function') {
					return linkNavBar()
				} else {
					return linkNavBar
				}
			}
			return false
		})
	})

	function hasChildren(route: RouteRecordRaw): boolean {
		return childLinks(route).length !== 0
	}

	function childLinks(route: RouteRecordRaw): RouteRecordRaw[] {
		if (!route.children) {
			return []
		}
		return route.children.filter((c: RouteRecordRaw) => {
			const meta = c?.meta as LinkRouteMeta
			// Check if the child has a linkLabel
			if (!meta?.linkLabel) {
				return false
			}
			// Check if the child has permission restrictions via linkNavBar
			const linkNavBar = meta.linkNavBar
			if (linkNavBar !== undefined) {
				if (typeof linkNavBar === 'function') {
					return linkNavBar()
				} else {
					return linkNavBar
				}
			}
			// If no linkNavBar is defined, show the link by default
			return true
		})
	}
</script>

<template>
	<div id="navbarStandard" class="navbar-collapse scrollbar collapse" style="">
		<ul class="navbar-nav" data-top-nav-dropdowns="data-top-nav-dropdowns">
			<li
				v-for="parentLink in parentLinks"
				:key="parentLink.name"
				class="nav-item"
				:class="hasChildren(parentLink) ? 'dropdown' : ''"
			>
				<div v-if="hasChildren(parentLink)">
					<RouterLink
						:id="'navbar' + String(parentLink.name)"
						class="nav-link dropdown-toggle"
						active-class="active"
						:to="{ path: parentLink.path }"
						role="button"
						data-bs-toggle="dropdown"
						aria-expanded="false"
						data-bs-auto-close="true"
						exact
						>{{ (parentLink.meta as LinkRouteMeta).linkLabel }}
					</RouterLink>
					<ul class="dropdown-menu py-0">
						<div class="py-2 rounded-3">
							<li v-for="childLink in childLinks(parentLink)" :key="childLink.name">
								<div v-if="hasChildren(childLink)" class="dropend">
									<RouterLink
										class="dropdown-item dropdown-toggle"
										exact-active-class="active"
										:to="{ name: childLink.name }"
										role="button"
										data-bs-toggle="dropdown"
										aria-expanded="false"
										exact
										@click.prevent.stop
										>{{ (childLink.meta as LinkRouteMeta).linkLabel }}
									</RouterLink>
									<ul class="dropdown-menu py-0">
										<div class="py-2 rounded-3">
											<li
												v-for="grandchildLink in childLinks(childLink)"
												:key="grandchildLink.name"
											>
												<RouterLink
													class="dropdown-item"
													exact-active-class="active"
													:to="{ name: grandchildLink.name }"
													>{{ (grandchildLink.meta as LinkRouteMeta).linkLabel }}
												</RouterLink>
											</li>
										</div>
									</ul>
								</div>
								<RouterLink
									v-else
									class="dropdown-item"
									exact-active-class="active"
									:to="{ name: childLink.name }"
									exact
									>{{ (childLink.meta as LinkRouteMeta).linkLabel }}
								</RouterLink>
							</li>
						</div>
					</ul>
				</div>
				<RouterLink v-else class="nav-link" exact-active-class="active" exact :to="{ path: parentLink.path }"
					>{{ (parentLink.meta as LinkRouteMeta).linkLabel }}
				</RouterLink>
			</li>
		</ul>
	</div>
</template>
